import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { usePageContents } from '../../contexts/PageContentContext';

const SignupTerms: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getPageContent } = usePageContents();

  const user_type = (location.state as any)?.user_type;

  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreeThirdParty, setAgreeThirdParty] = useState(false);
  const [agreeAll, setAgreeAll] = useState(false);

  // user_type이 없으면 처음부터 다시 시작
  useEffect(() => {
    if (!user_type) {
      navigate('/signup');
    }
  }, [user_type, navigate]);

  // 전체 동의 체크박스 핸들러
  const handleAgreeAll = (checked: boolean) => {
    setAgreeAll(checked);
    setAgreeTerms(checked);
    setAgreePrivacy(checked);
    setAgreeThirdParty(checked);
  };

  // 개별 체크박스 변경 시 전체 동의 상태 업데이트
  useEffect(() => {
    if (agreeTerms && agreePrivacy && agreeThirdParty) {
      setAgreeAll(true);
    } else {
      setAgreeAll(false);
    }
  }, [agreeTerms, agreePrivacy, agreeThirdParty]);

  const handleNext = () => {
    if (!agreeTerms || !agreePrivacy || !agreeThirdParty) {
      alert('모든 약관에 동의해주세요.');
      return;
    }

    navigate('/signup/form', {
      state: {
        user_type,
        agreements: {
          terms: agreeTerms,
          privacy: agreePrivacy,
          thirdParty: agreeThirdParty,
        }
      }
    });
  };

  if (!user_type) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <div className="flex-1 bg-gray-50 py-12 mt-20">
        <div className="max-w-5xl mx-auto px-4">
        {/* Contents Head */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <h3 className="text-3xl font-bold text-gray-900">
              {user_type === 'minor' ? '14세 미만 회원가입' : '통합회원가입'}
            </h3>
            <div className="flex gap-4 text-sm">
              <Link to="/login" className="text-gray-600 hover:text-[#48c9a6]">로그인</Link>
              <Link to="/signup" className="text-[#48c9a6] font-medium">
                {user_type === 'minor' ? '14세 미만 회원가입' : '통합회원가입'}
              </Link>
            </div>
          </div>
          <div className="h-1 bg-[#48c9a6] w-20 mt-4"></div>
        </div>

        {/* Contents Body */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          {/* 약관 동의 타이틀 */}
          <div className="mb-8 pb-4 border-b border-gray-200">
            <span className="text-xl font-bold text-gray-900">약관 동의</span>
          </div>

          {/* 전체 동의 */}
          <div className="mb-6 p-4 bg-[#48c9a6]/10 border-2 border-[#48c9a6] rounded-lg">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={agreeAll}
                onChange={(e) => handleAgreeAll(e.target.checked)}
                className="w-5 h-5 text-[#48c9a6] border-gray-300 rounded focus:ring-[#48c9a6]"
              />
              <span className="ml-3 text-base font-bold text-gray-900">
                전체 약관에 동의합니다
              </span>
            </label>
          </div>

          {/* 이용약관 (필수) */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-3">
              <h4 className="terms-title text-lg font-bold text-gray-900">이용약관</h4>
              <span className="text-sm font-medium text-red-600">(필수)</span>
            </div>
            <div className="border border-gray-300 rounded-lg p-4 h-64 overflow-y-auto bg-gray-50 text-sm text-gray-700 leading-relaxed mb-3" tabIndex={0}>
              {getPageContent('terms_service')?.description ? (
                <div
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: getPageContent('terms_service')?.description || '' }}
                />
              ) : (
                <div className="space-y-4">
                  <div>
                    <h5 className="font-bold text-gray-900 mb-2">제 1 장 총칙</h5>
                    <div className="mb-3">
                      <p className="font-semibold">제 1 조 (목적)</p>
                      <p>본 약관은 동두천시글로벌인재교육센터 홈페이지(이하 "홈페이지")가 제공하는 모든 서비스(이하 "서비스")의 이용조건 및 절차, 이용자와 홈페이지의 권리·의무·책임사항과 기타 필요한 사항을 규정함을 목적으로 합니다.</p>
                    </div>
                    <div className="mb-3">
                      <p className="font-semibold">제 2 조 (약관의 효력과 변경)</p>
                      <p className="mb-1">이용자가 본 약관 내용에 동의하는 경우, 홈페이지의 서비스 제공 행위 및 이용자의 서비스 이용 행위에 본 약관이 우선적으로 적용됩니다.</p>
                      <p className="mb-1">홈페이지는 본 약관을 사전 고지 없이 변경할 수 있으며, 변경된 약관은 홈페이지 내 또는 회원에게 공지함으로써 효력이 발생합니다.</p>
                    </div>
                    <div className="mb-3">
                      <p className="font-semibold">제 3 조 (약관 외 준칙)</p>
                      <p>본 약관에 명시되지 않은 사항은 관계법령 및 서비스별 안내에서 정하는 바에 따릅니다.</p>
                    </div>
                    <div className="mb-3">
                      <p className="font-semibold">제 4 조 (용어의 정의)</p>
                      <p className="mb-1">① "홈페이지"라 함은 동두천시글로벌인재교육센터가 운영하는 인터넷 웹사이트를 말합니다.</p>
                      <p className="mb-1">② "이용자"라 함은 홈페이지에 접속하여 서비스를 이용하는 회원 및 비회원을 말합니다.</p>
                      <p className="mb-1">③ "회원"이라 함은 홈페이지에 개인정보를 제공하여 회원등록을 한 자로서, 홈페이지의 서비스를 계속적으로 이용할 수 있는 자를 말합니다.</p>
                    </div>
                  </div>
                  <div>
                    <h5 className="font-bold text-gray-900 mb-2">제 2 장 서비스 이용계약</h5>
                    <div className="mb-3">
                      <p className="font-semibold">제 5 조 (이용계약의 성립)</p>
                      <p className="mb-1">① 이용계약은 이용자가 본 약관에 동의하고 회원가입 신청을 하면 홈페이지가 이를 승낙함으로써 성립합니다.</p>
                      <p className="mb-1">② 홈페이지는 다음 각 호에 해당하는 신청에 대해서는 승낙을 거부하거나 사후에 이용계약을 해지할 수 있습니다.</p>
                    </div>
                    <div className="mb-3">
                      <p className="font-semibold">제 6 조 (서비스의 내용)</p>
                      <p>홈페이지는 교육 프로그램 안내, 수강 신청, 공지사항 등의 서비스를 제공합니다.</p>
                    </div>
                  </div>
                  <div>
                    <h5 className="font-bold text-gray-900 mb-2">제 3 장 의무</h5>
                    <div className="mb-3">
                      <p className="font-semibold">제 7 조 (홈페이지의 의무)</p>
                      <p className="mb-1">① 홈페이지는 안정적인 서비스 제공을 위하여 노력합니다.</p>
                      <p className="mb-1">② 홈페이지는 이용자의 개인정보를 보호하기 위하여 보안시스템을 갖추어야 합니다.</p>
                    </div>
                    <div className="mb-3">
                      <p className="font-semibold">제 8 조 (이용자의 의무)</p>
                      <p className="mb-1">① 이용자는 본 약관에서 규정하는 사항과 홈페이지가 공지하는 사항을 준수하여야 합니다.</p>
                      <p className="mb-1">② 이용자는 타인의 개인정보를 도용하거나 부정하게 사용해서는 안 됩니다.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={agreeTerms}
                onChange={(e) => setAgreeTerms(e.target.checked)}
                className="w-4 h-4 text-[#48c9a6] border-gray-300 rounded focus:ring-[#48c9a6]"
              />
              <span className="ml-2 text-sm text-gray-700">이용약관에 동의하십니까?</span>
            </label>
          </div>

          {/* 개인정보 수집, 이용 동의 (필수) */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-3">
              <h4 className="terms-title text-lg font-bold text-gray-900">개인정보 수집, 이용 동의</h4>
              <span className="text-sm font-medium text-red-600">(필수)</span>
            </div>
            <div className="border border-gray-300 rounded-lg p-4 h-64 overflow-y-auto bg-gray-50 text-sm text-gray-700 leading-relaxed mb-3" tabIndex={0}>
              {getPageContent('terms_privacy')?.description ? (
                <div
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: getPageContent('terms_privacy')?.description || '' }}
                />
              ) : (
                <div className="space-y-4">
                  <div>
                    <h5 className="font-bold text-gray-900 mb-2">개인정보 수집·이용 안내</h5>
                    <p className="mb-3">동두천시글로벌인재교육센터는 「개인정보보호법」 등 관련 법령에 따라 개인정보 수집·이용에 대한 동의를 받고 있으며, 수집된 개인정보는 아래의 목적 범위 내에서만 이용됩니다.</p>

                    <div className="mb-4">
                      <p className="font-semibold mb-2">1. 개인정보의 수집·이용 목적</p>
                      <p className="mb-1">센터는 다음의 목적을 위하여 최소한의 개인정보를 수집·이용합니다.</p>
                      <p className="mb-1"><strong>① 화상·교육 서비스 제공</strong></p>
                      <p className="mb-1 ml-4">- 화상관리 서비스 제공</p>
                      <p className="mb-1 ml-4">- 이용 이력 저장, 녹화 송신</p>
                      <p className="mb-1 ml-4">- 학습 기록 관리 및 학부모 피드백 제공</p>
                      <p className="mb-1"><strong>② 회원 가입 및 관리</strong></p>
                      <p className="mb-1 ml-4">- 본인 확인 및 회원 식별</p>
                      <p className="mb-1 ml-4">- 만 14세 미만 아동 개인정보 처리 시 법정대리인 동의 확인</p>
                      <p className="mb-1 ml-4">- 공지, 문의 대응, 서비스 운영</p>
                      <p className="mb-1"><strong>③ 마케팅 및 광고 활용(선택 동의)</strong></p>
                      <p className="mb-1 ml-4">※ 미동의 시에도 서비스 이용에는 제한이 없습니다.</p>
                      <p className="mb-1 ml-4">- 이벤트·프로모션 안내</p>
                      <p className="mb-1 ml-4">- 광고성 정보 제공</p>
                      <p className="mb-1 ml-4">- 서비스 이용 통계·분석</p>
                      <p className="ml-4">- 관계사 상품 소식 제공</p>
                    </div>

                    <div className="mb-4">
                      <p className="font-semibold mb-2">2. 개인정보의 수집 항목 및 수집 방법</p>
                      <p className="mb-2">센터는 아래와 같이 개인정보를 수집합니다.</p>
                      <p className="font-medium mb-1">(1) 필수 수집 항목</p>
                      <table className="w-full border-collapse border border-gray-300 mb-3">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="border border-gray-300 p-2 text-left">구분</th>
                            <th className="border border-gray-300 p-2 text-left">수집 항목</th>
                            <th className="border border-gray-300 p-2 text-left">수집 방법</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="border border-gray-300 p-2">학습생 정보</td>
                            <td className="border border-gray-300 p-2">아이디, 비밀번호, 이름, 생년월일, 성별, 학교명, 학년</td>
                            <td className="border border-gray-300 p-2">홈페이지 회원가입</td>
                          </tr>
                          <tr>
                            <td className="border border-gray-300 p-2">자동수집 정보</td>
                            <td className="border border-gray-300 p-2">접속일시, 쿠키 등 서비스 이용기록</td>
                            <td className="border border-gray-300 p-2">홈페이지 자동수집</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <div className="mb-4">
                      <p className="font-semibold mb-2">3. 개인정보의 보유 및 이용 기간</p>
                      <p className="mb-2">센터는 원칙적으로 개인정보의 수집·이용 목적이 달성되면 지체 없이 파기합니다. 다만 아래의 경우에는 명시된 기간 동안 보관합니다.</p>
                      <p className="font-medium mb-1">(1) 기본 보유기간</p>
                      <table className="w-full border-collapse border border-gray-300 mb-3">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="border border-gray-300 p-2 text-left">개인정보 유형</th>
                            <th className="border border-gray-300 p-2 text-left">보유 목적</th>
                            <th className="border border-gray-300 p-2 text-left">보유기간</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="border border-gray-300 p-2">학습생 정보</td>
                            <td className="border border-gray-300 p-2">이용자 식별, 회원 서비스 제공</td>
                            <td className="border border-gray-300 p-2">회원 탈퇴 시 즉시 파기 (단, ID는 부정사용 방지 목적으로 영구보관)</td>
                          </tr>
                          <tr>
                            <td className="border border-gray-300 p-2">자동수집 정보</td>
                            <td className="border border-gray-300 p-2">서비스 이용기록 및 통계 분석</td>
                            <td className="border border-gray-300 p-2">목적 달성 시 즉시 파기</td>
                          </tr>
                          <tr>
                            <td className="border border-gray-300 p-2">결제정보</td>
                            <td className="border border-gray-300 p-2">주문, 환불, 결제 서비스 제공</td>
                            <td className="border border-gray-300 p-2">법정 의무기간 보관</td>
                          </tr>
                        </tbody>
                      </table>
                      <p className="font-medium mb-1">(2) 법령에 따른 의무 보관 기간</p>
                      <table className="w-full border-collapse border border-gray-300 mb-3">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="border border-gray-300 p-2 text-left">관련 법령</th>
                            <th className="border border-gray-300 p-2 text-left">보유 항목</th>
                            <th className="border border-gray-300 p-2 text-left">보유기간</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="border border-gray-300 p-2" rowSpan={4}>전자상거래 등에서의 소비자 보호에 관한 법률</td>
                            <td className="border border-gray-300 p-2">계약·청약철회 기록</td>
                            <td className="border border-gray-300 p-2">5년</td>
                          </tr>
                          <tr>
                            <td className="border border-gray-300 p-2">대금결제 및 재화 공급 기록</td>
                            <td className="border border-gray-300 p-2">5년</td>
                          </tr>
                          <tr>
                            <td className="border border-gray-300 p-2">소비자 불만 또는 분쟁처리 기록</td>
                            <td className="border border-gray-300 p-2">3년</td>
                          </tr>
                          <tr>
                            <td className="border border-gray-300 p-2">표시·광고에 관한 기록</td>
                            <td className="border border-gray-300 p-2">6개월</td>
                          </tr>
                          <tr>
                            <td className="border border-gray-300 p-2">통신비밀보호법</td>
                            <td className="border border-gray-300 p-2">접속기록(IP 등)</td>
                            <td className="border border-gray-300 p-2">3개월</td>
                          </tr>
                        </tbody>
                      </table>
                      <p className="font-medium mb-1">(3) 장기 미이용자 파기(개인정보 유효기간제)</p>
                      <p>1년 동안 서비스 이용이 없는 회원의 정보는 별도 분리 보관 또는 파기합니다.</p>
                    </div>

                    <div className="mb-4">
                      <p className="font-semibold mb-2">4. 마케팅 정보 활용(선택 동의)</p>
                      <p className="mb-2">※ 선택 동의이며, 동의하지 않아도 가입·서비스 이용이 모두 가능합니다.</p>
                      <table className="w-full border-collapse border border-gray-300 mb-3">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="border border-gray-300 p-2 text-left">이용 목적</th>
                            <th className="border border-gray-300 p-2 text-left">이용 항목</th>
                            <th className="border border-gray-300 p-2 text-left">보유 기간</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="border border-gray-300 p-2">교육 프로그램 안내, 이벤트·홍보 정보 제공</td>
                            <td className="border border-gray-300 p-2">학부모 성명, 연락처(휴대폰), 자녀 학년, 이메일</td>
                            <td className="border border-gray-300 p-2">회원 탈퇴 또는 마케팅 동의 철회 시까지</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <div>
                      <p className="font-semibold mb-2">5. 개인정보 보호책임자</p>
                      <p className="mb-1">센터는 개인정보의 안전한 처리를 위해 아래와 같이 개인정보 보호책임자를 지정하고 있습니다.</p>
                      <table className="w-full border-collapse border border-gray-300 mb-3">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="border border-gray-300 p-2 text-left">구분</th>
                            <th className="border border-gray-300 p-2 text-left">개인정보 보호 관리 책임자</th>
                            <th className="border border-gray-300 p-2 text-left">개인정보 보호 관리자</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="border border-gray-300 p-2">이름</td>
                            <td className="border border-gray-300 p-2">김은정</td>
                            <td className="border border-gray-300 p-2">김정인</td>
                          </tr>
                          <tr>
                            <td className="border border-gray-300 p-2">직위</td>
                            <td className="border border-gray-300 p-2">센터장</td>
                            <td className="border border-gray-300 p-2">팀장</td>
                          </tr>
                          <tr>
                            <td className="border border-gray-300 p-2">전화번호</td>
                            <td className="border border-gray-300 p-2">031-866-5670</td>
                            <td className="border border-gray-300 p-2">031-866-5670</td>
                          </tr>
                        </tbody>
                      </table>
                      <p>고객님은 언제든지 문의, 열람, 정정, 삭제, 처리정지 요청 등을 개인정보 담당자에게 문의하실 수 있습니다.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={agreePrivacy}
                onChange={(e) => setAgreePrivacy(e.target.checked)}
                className="w-4 h-4 text-[#48c9a6] border-gray-300 rounded focus:ring-[#48c9a6]"
              />
              <span className="ml-2 text-sm text-gray-700">개인정보 수집 및 이용에 동의하십니까?</span>
            </label>
          </div>

          {/* 개인정보 제3자 제공 동의 */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-3">
              <h4 className="terms-title text-lg font-bold text-gray-900">개인정보 제3자 제공 동의</h4>
            </div>
            <div className="border border-gray-300 rounded-lg p-4 h-64 overflow-y-auto bg-gray-50 text-sm text-gray-700 leading-relaxed mb-3" tabIndex={0}>
              {getPageContent('terms_third_party')?.description ? (
                <div
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: getPageContent('terms_third_party')?.description || '' }}
                />
              ) : (
                <div className="space-y-4">
                  <div>
                    <h5 className="font-bold text-gray-900 mb-2">개인정보 제3자 제공 안내</h5>
                    <p className="mb-3">본 문서는 「개인정보보호법」 제17조(제3자 제공), 제26조(업무 위탁), 제15조(개인정보의 수집·이용)에 따라, 센터가 개인정보를 제공하거나 위탁할 때 필수적으로 안내해야 하는 사항을 포함하고 있습니다.</p>

                    <div className="mb-4">
                      <p className="font-semibold mb-2">1. 개인정보의 제3자 제공</p>
                      <p className="mb-2">동두천시글로벌인재교육센터(이하 "센터")는 원칙적으로 회원의 개인정보를 이용자 동의 없이 제3자에게 제공하지 않습니다. 다만 아래의 경우에는 예외로 할 수 있습니다.</p>

                      <p className="font-medium mb-1">① 회원이 사전에 명시적으로 동의한 경우</p>
                      <p className="mb-2 ml-4">센터는 제3자 제공을 위해 아래의 내용을 사전에 고지하고 회원의 개별 동의를 얻습니다.</p>
                      <ul className="mb-2 ml-8 list-disc">
                        <li>제공받는 자</li>
                        <li>제공 목적</li>
                        <li>제공 항목</li>
                        <li>보유 및 이용기간</li>
                      </ul>
                      <p className="mb-2 ml-4">동의하지 않을 경우 해당 제3자에게 개인정보는 제공되지 않습니다.</p>

                      <p className="font-medium mb-1">② 법령에 근거가 있는 경우</p>
                      <p className="mb-2 ml-4">관계 법령의 규정에 의하거나, 수사 목적으로 관계 기관이 적법한 절차에 따라 요구한 경우</p>

                      <p className="font-medium mb-1">③ 영업 양수도 등의 사유로 이전이 필요한 경우</p>
                      <p className="ml-4">센터는 개인정보 이전 사실을 사전에 고지하고, 회원에게 동의 철회권을 부여합니다.</p>
                    </div>

                    <div className="mb-4">
                      <p className="font-semibold mb-2">2. 제3자 제공 현황</p>
                      <p className="mb-2">※ 제3자 제공 동의는 선택 사항이며, 동의하지 않아도 회원가입 및 기본 서비스 이용에 제한이 없습니다. 단, 제공 업체가 제공하는 부가 서비스(교육 프로그램 안내 등) 이용이 제한될 수 있습니다.</p>
                      <table className="w-full border-collapse border border-gray-300">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="border border-gray-300 p-2 text-left">제공받는 자</th>
                            <th className="border border-gray-300 p-2 text-left">제공 목적</th>
                            <th className="border border-gray-300 p-2 text-left">제공 항목</th>
                            <th className="border border-gray-300 p-2 text-left">보유 및 이용 기간</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="border border-gray-300 p-2">㈜한원글로벌</td>
                            <td className="border border-gray-300 p-2">교육 프로그램 및 서비스 정보 제공</td>
                            <td className="border border-gray-300 p-2">이름, 휴대폰 번호, 생년월일, 이메일, 학년, 학교명, 보호자 이름</td>
                            <td className="border border-gray-300 p-2">회원 탈퇴 또는 동의 철회 시까지</td>
                          </tr>
                          <tr>
                            <td className="border border-gray-300 p-2">솔라피(SOLAPI)</td>
                            <td className="border border-gray-300 p-2">본인확인(휴대폰 인증 등)</td>
                            <td className="border border-gray-300 p-2">성명, 휴대폰 번호</td>
                            <td className="border border-gray-300 p-2">계약 종료 시까지</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={agreeThirdParty}
                onChange={(e) => setAgreeThirdParty(e.target.checked)}
                className="w-4 h-4 text-[#48c9a6] border-gray-300 rounded focus:ring-[#48c9a6]"
              />
              <span className="ml-2 text-sm text-gray-700">개인정보 제3자 제공에 동의하십니까?</span>
            </label>
          </div>

          {/* 안내 문구 */}
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              ⚠️ 모든 약관에 동의하지 않으실 경우 회원가입이 제한됩니다.
            </p>
          </div>

          {/* 버튼 영역 */}
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/signup', { state: { user_type } })}
              className="flex-1 py-3 px-4 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              이전 단계
            </button>
            <button
              onClick={handleNext}
              disabled={!agreeTerms || !agreePrivacy || !agreeThirdParty}
              className="flex-1 py-3 px-4 border border-transparent text-base font-medium rounded-lg text-white bg-[#48c9a6] hover:bg-[#3ab89a] disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              다음 단계
            </button>
          </div>
        </div>
      </div>
      </div>

      <Footer />
    </div>
  );
};

export default SignupTerms;
